import React from 'react';

/**
 * SubscriptionExpiredModal
 * Shown when a user hits a subscription quota limit or tries to access a locked module.
 *
 * Props:
 *  - isOpen {boolean}         - whether the modal is visible
 *  - onClose {function}       - called when user dismisses the modal
 *  - type {'quota'|'module'}  - the reason for showing. 'quota' = limit reached, 'module' = module not in plan
 *  - limitName {string}       - e.g. "Products", "Users", "Invoices", "Business Locations"
 *  - moduleName {string}      - e.g. "CRM", "WooCommerce", "Repair", "AI Assistance"
 *  - current {number}         - current usage count (for quota)
 *  - allowed {number}         - allowed count (for quota)
 *  - packageName {string}     - current package name
 */
const SubscriptionExpiredModal = ({
  isOpen,
  onClose,
  type = 'quota',
  limitName = 'items',
  moduleName = '',
  current = 0,
  allowed = 0,
  packageName = 'Current Plan',
}) => {
  if (!isOpen) return null;

  const isQuota = type === 'quota';

  const title = isQuota
    ? `${limitName} Limit Reached`
    : `${moduleName} Module Not Included`;

  const subtitle = isQuota
    ? `Your current plan (${packageName}) allows up to ${allowed} ${limitName.toLowerCase()}.`
    : `Your current plan (${packageName}) does not include the ${moduleName} module.`;

  const description = isQuota
    ? `You have already created ${current} out of ${allowed} ${limitName.toLowerCase()}. To add more, please upgrade your subscription plan.`
    : `Upgrade your subscription plan to unlock the ${moduleName} module and gain access to all its features.`;

  return (
    <>
      <style>{`
        .sub-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(4px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: subModalFadeIn 0.2s ease;
        }
        @keyframes subModalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .sub-modal-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
          max-width: 480px;
          width: 100%;
          overflow: hidden;
          animation: subModalSlideUp 0.25s ease;
        }
        @keyframes subModalSlideUp {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .sub-modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem 2rem 1.5rem;
          text-align: center;
          color: #fff;
          position: relative;
        }
        .sub-modal-header-icon {
          width: 64px;
          height: 64px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin: 0 auto 1rem;
          border: 2px solid rgba(255,255,255,0.3);
        }
        .sub-modal-header h2 {
          margin: 0 0 0.25rem;
          font-size: 1.35rem;
          font-weight: 700;
        }
        .sub-modal-header p {
          margin: 0;
          font-size: 0.9rem;
          opacity: 0.88;
        }
        .sub-modal-close-btn {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          background: rgba(255,255,255,0.2);
          border: none;
          color: #fff;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .sub-modal-close-btn:hover { background: rgba(255,255,255,0.35); }
        .sub-modal-body {
          padding: 1.75rem 2rem;
        }
        .sub-modal-desc {
          color: #475569;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          text-align: center;
        }
        .sub-modal-quota-bar {
          background: #f1f5f9;
          border-radius: 8px;
          padding: 1rem 1.25rem;
          margin-bottom: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .sub-modal-quota-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          font-weight: 600;
          color: #334155;
        }
        .sub-modal-progress-track {
          height: 8px;
          background: #e2e8f0;
          border-radius: 99px;
          overflow: hidden;
        }
        .sub-modal-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #ef4444, #dc2626);
          border-radius: 99px;
          width: 100%;
          transition: width 0.4s ease;
        }
        .sub-modal-benefits {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-bottom: 1.75rem;
        }
        .sub-modal-benefit-row {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          color: #475569;
          font-size: 0.9rem;
        }
        .sub-modal-benefit-check {
          width: 20px;
          height: 20px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #fff;
          font-size: 0.75rem;
        }
        .sub-modal-actions {
          display: flex;
          gap: 0.75rem;
        }
        .sub-modal-btn-secondary {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          background: #fff;
          color: #64748b;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .sub-modal-btn-secondary:hover {
          border-color: #94a3b8;
          color: #334155;
        }
        .sub-modal-btn-primary {
          flex: 2;
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-decoration: none;
        }
        .sub-modal-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
        .sub-modal-btn-primary:active { transform: translateY(0); }
      `}</style>

      <div className="sub-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="sub-modal-card" role="dialog" aria-modal="true" aria-labelledby="sub-modal-title">

          {/* Header */}
          <div className="sub-modal-header">
            <button className="sub-modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
            <div className="sub-modal-header-icon">
              {isQuota ? '⚠️' : '🔒'}
            </div>
            <h2 id="sub-modal-title">{title}</h2>
            <p>{subtitle}</p>
          </div>

          {/* Body */}
          <div className="sub-modal-body">

            <p className="sub-modal-desc">{description}</p>

            {/* Quota progress bar */}
            {isQuota && allowed > 0 && (
              <div className="sub-modal-quota-bar">
                <div className="sub-modal-quota-label">
                  <span>{limitName} Used</span>
                  <span style={{ color: '#ef4444' }}>{current} / {allowed}</span>
                </div>
                <div className="sub-modal-progress-track">
                  <div
                    className="sub-modal-progress-fill"
                    style={{ width: `${Math.min((current / allowed) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="sub-modal-benefits">
              {[
                'Upgrade for unlimited capacity',
                'Access all premium modules',
                'Priority support & updates',
                'No interruption to your business',
              ].map((benefit) => (
                <div className="sub-modal-benefit-row" key={benefit}>
                  <div className="sub-modal-benefit-check">✓</div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="sub-modal-actions">
              <button className="sub-modal-btn-secondary" onClick={onClose}>
                Maybe Later
              </button>
              <a
                className="sub-modal-btn-primary"
                href="/settings/subscription"
                onClick={onClose}
              >
                ⚡ Upgrade Plan
              </a>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SubscriptionExpiredModal;
